import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LangProvider } from "@/lib/lang";
import { ContentProvider } from "@/lib/content";
import Layout from "@/components/places/Layout";
import AnalyticsLoader from "@/components/places/AnalyticsLoader";
import Home from "@/pages/Home";
import GeoLanding from "@/pages/GeoLanding";
import LandingPage from "@/pages/LandingPage";
import Sobre from "@/pages/Sobre";
import Servicos from "@/pages/Servicos";
import Cases from "@/pages/Cases";
import CaseDetail from "@/pages/CaseDetail";
import Insights from "@/pages/Insights";
import Carreiras from "@/pages/Carreiras";
import InsightDetail from "@/pages/InsightDetail";
import Contato from "@/pages/Contato";
import Timeline38Page from "@/pages/Timeline38Page";
import Privacidade from "@/pages/Privacidade";
import NotFound from "@/pages/NotFound";
import AdminApp from "@/admin/AdminApp";
import ClienteApp from "@/cliente/ClienteApp";
import { LOCALE_PREFIXES, DEFAULT_LOCALE, findLocaleByPrefix } from "@/lib/locales";

/** Guard: if the current URL prefix is not one of the supported locales, redirect
 * to the default (pt-br) equivalent, preserving the remaining path + query.
 * NOTE: no longer used at the top level — explicit /pt-br/*, /pt-pt/*, /es-py/*
 * routes replaced /:locale/* to avoid collision with /admin/* and /cliente/*
 * (react-router v6 route scoring outranked our splat). Kept for potential future
 * reuse if we need a runtime prefix guard. */
function LocaleGate({ children }) {
  const { locale: prefixParam } = useParams();
  const { search, hash, pathname } = useLocation();
  const found = findLocaleByPrefix(prefixParam);
  if (!found) {
    return <Navigate to={`/${DEFAULT_LOCALE.prefix}${pathname}${search}${hash}`} replace />;
  }
  return children;
}

/** Redirects a legacy (unprefixed) URL to the pt-br equivalent, preserving
 * the path (e.g. /cases/foo → /pt-br/cases/foo) and query string. */
function LegacyRedirect({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`/${DEFAULT_LOCALE.prefix}${to}${search}${hash}`} replace />;
}

/** Regional slug groups — accept BOTH the canonical BR slugs AND their localized
 * aliases (including the EN ones so the /en/* prefix reaches the right page).
 * This gives clean regional URLs while keeping a single React tree. */
const CASES_SLUGS   = ["cases", "projetos", "casos"];
const SERVICES_SLUGS = ["servicos", "servicios", "services"];
const SOBRE_SLUGS = ["sobre", "about"];
const CONTACT_SLUGS = ["contato", "contacto", "contact"];
const CAREERS_SLUGS = ["carreiras", "carreras", "careers"];
const PRIVACY_SLUGS = ["privacidade", "privacidad", "privacy"];
const TIMELINE_SLUGS = ["38-anos", "38-years"];

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ContentProvider>
          <AnalyticsLoader />
          <LangProvider>
            <Routes>
              <Route path="/admin/*" element={<AdminApp />} />
              <Route path="/cliente/*" element={<ClienteApp />} />

              {/* NEW — explicit regional prefix routes: /pt-br/*, /pt-pt/*, /es-py/*.
                  IMPORTANT: three explicit routes (not /:locale/*) so /admin/* and
                  /cliente/* are not stolen by react-router v6 route scoring. */}
              {LOCALE_PREFIXES.map((prefix) => (
                <Route key={`loc-${prefix}`} path={`/${prefix}/*`} element={<Layout />}>
                  <Route index element={<Home />} />
                  {SOBRE_SLUGS.map((s) => (<Route key={`ab-${prefix}-${s}`} path={s} element={<Sobre />} />))}
                  {SERVICES_SLUGS.map((s) => (<Route key={`sv-${prefix}-${s}`} path={s} element={<Servicos />} />))}
                  {CASES_SLUGS.map((s) => (<Route key={`c-${prefix}-${s}`} path={s} element={<Cases />} />))}
                  {CASES_SLUGS.map((s) => (<Route key={`cs-${prefix}-${s}`} path={`${s}/:slug`} element={<CaseDetail />} />))}
                  <Route path="insights" element={<Insights />} />
                  <Route path="insights/:slug" element={<InsightDetail />} />
                  {CAREERS_SLUGS.map((s) => (<Route key={`k-${prefix}-${s}`} path={s} element={<Carreiras />} />))}
                  {CONTACT_SLUGS.map((s) => (<Route key={`ct-${prefix}-${s}`} path={s} element={<Contato />} />))}
                  {TIMELINE_SLUGS.map((s) => (<Route key={`t-${prefix}-${s}`} path={s} element={<Timeline38Page />} />))}
                  {PRIVACY_SLUGS.map((s) => (<Route key={`p-${prefix}-${s}`} path={s} element={<Privacidade />} />))}
                  <Route path="*" element={<NotFound />} />
                </Route>
              ))}

              {/* LEGACY — old unprefixed routes redirect (soft 301) to /pt-br/* preserving path + query */}
              <Route path="/" element={<Navigate to={`/${DEFAULT_LOCALE.prefix}`} replace />} />
              <Route path="/sobre" element={<LegacyRedirect to="/sobre" />} />
              <Route path="/servicos" element={<LegacyRedirect to="/servicos" />} />
              <Route path="/cases" element={<LegacyRedirect to="/cases" />} />
              <Route path="/cases/:slug" element={<LegacyCasesRedirect />} />
              <Route path="/insights" element={<LegacyRedirect to="/insights" />} />
              <Route path="/insights/:slug" element={<LegacyInsightsRedirect />} />
              <Route path="/carreiras" element={<LegacyRedirect to="/carreiras" />} />
              <Route path="/contato" element={<LegacyRedirect to="/contato" />} />
              <Route path="/38-anos" element={<LegacyRedirect to="/38-anos" />} />
              <Route path="/privacidade" element={<LegacyRedirect to="/privacidade" />} />

              {/* Landing Pages — sem Layout (LP tem seu próprio hero/rodapé livre). */}
              <Route path="/lp/:slug" element={<LandingPage />} />

              {/* GEO-SEO — landings de cidade.
                  Duas rotas apontam pro mesmo componente:
                    /:cidade                          (curta — ex: /goiania)
                    /marketing-imobiliario-:cidade    (canonical, keyword-rich)
                  React Router 6 não parseia `:param` grudado em prefixo com `-`,
                  então usamos um único catch-all `/:citySlug` no fim e o próprio
                  <GeoLanding> distingue os dois padrões via a string do slug. */}
              <Route path="/:citySlug" element={<Layout />}>
                <Route index element={<GeoLanding />} />
              </Route>

              <Route path="*" element={<Navigate to={`/${DEFAULT_LOCALE.prefix}`} replace />} />
            </Routes>
          </LangProvider>
        </ContentProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

function LegacyCasesRedirect() {
  const { slug } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${DEFAULT_LOCALE.prefix}/cases/${slug}${search}${hash}`} replace />;
}

function LegacyInsightsRedirect() {
  const { slug } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${DEFAULT_LOCALE.prefix}/insights/${slug}${search}${hash}`} replace />;
}

export default App;
// re-export prefixes for tools/tests
export { LOCALE_PREFIXES };
